// TicketedEventTagForm.tsx
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import service from '../../../service';
import { cn } from '../../../utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { CircleAlert } from 'lucide-react';

const TagSchema = z.object({
  name: z.string().min(2, 'Please enter at least 2 characters'),
  description: z
    .string()
    .max(500, 'Keep it under 500 characters')
    .optional()
    .or(z.literal('')),
});

export type TicketedEventTagFormValues = z.infer<typeof TagSchema>;

type Props = {
  className?: string;
  defaultValues?: Partial<TicketedEventTagFormValues>;
  submitLabel?: string;
  /** Invalidate this query key after success */
  invalidateKey?: (string | number)[];
  onSuccess?: (created?: unknown) => void;

  /** NEW: form id so parent buttons can submit with form={formId} */
  formId?: string;
  /** NEW: hide internal Reset/Submit buttons (parent supplies them) */
  hideInternalActions?: boolean;
  /** When provided, switches to edit mode and calls updateTicketedEventTag */
  editId?: string;
};

export default function TicketedEventTagForm({
  className,
  defaultValues,
  submitLabel,
  invalidateKey = ['ticketed-event-tags'],
  onSuccess,

  formId = 'ticketed-event-tag-form',
  hideInternalActions = false,
  editId,
}: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!editId;
  const resolvedSubmitLabel =
    submitLabel ?? (isEditMode ? 'Update tag' : 'Create tag');

  const form = useForm<TicketedEventTagFormValues>({
    resolver: zodResolver(TagSchema),
    defaultValues: { name: '', description: '', ...defaultValues },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting },
  } = form;

  const mutation = useMutation({
    mutationFn: async (values: TicketedEventTagFormValues) => {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
      };
      if (isEditMode) {
        return service.updateTicketedEventTag(editId, payload);
      }
      return service.createTicketedEventTag(payload);
    },
    onSuccess: async (result) => {
      if (invalidateKey?.length) {
        await queryClient.invalidateQueries({ queryKey: invalidateKey });
      }
      onSuccess?.(result);
      toastSuccess(isEditMode ? 'Tag updated' : 'Tag created');
      if (!isEditMode) {
        reset({ name: '', description: '' });
      }
    },
    onError: (err: any) => {
      setError('root', {
        type: 'server',
        message:
          err?.message ||
          `Something went wrong while ${isEditMode ? 'updating' : 'creating'} the tag. Please try again.`,
      });
    },
  });

  const onSubmit = (values: TicketedEventTagFormValues) => {
    mutation.mutate(values);
  };

  const pending = isSubmitting || mutation.isPending;

  return (
    <div className={cn('max-w-xl', className)}>
      <Form {...form}>
        {/* IMPORTANT: expose id for external submit via <Button form={formId}/> */}
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Wine Pairings" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Optional: explain how this tag is used…"
                    className="resize-y"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* top-level error */}
          {form.formState.errors.root?.message ? (
            <Alert variant="destructive" className="my-4">
              <CircleAlert />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root?.message ??
                  'Error creating tag. Please try again later.'}
              </AlertDescription>
            </Alert>
          ) : null}

          {!hideInternalActions && (
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => reset()}
                disabled={pending}
              >
                Reset
              </Button>
              <Button type="submit" variant="primary" loading={pending}>
                {resolvedSubmitLabel}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
